import { PopperPlacementType } from "@mui/base/Popper";
import { ClickAwayListener } from "@mui/material";
import { genericMemo } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import classNames from "classnames";
import {
  CSSProperties,
  KeyboardEvent,
  UIEvent,
  useEffect,
  useState,
} from "react";
import { Dropdown } from "../../dropdown";
import { Flex, FlexProps } from "../../flex";
import { BaseRedoInput, RedoInputSize } from "../input/base-redo-text-input";
import { RedoDropdownInputSize } from "../select-dropdown/redo-single-select-dropdown-input";
import {
  RedoList,
  RedoListItem,
  RedoListItemSelectedSource,
  RedoListProps,
} from "./redo-list";
import * as css from "./redo-list-dropdown.module.css";
import { RedoListItemSize } from "./standard-redo-list-items";

export const RedoListDropdown = genericMemo(function RedoListDropdown<T>({
  dropdownOpen,
  setDropdownOpen,
  dropdownAnchor,
  "data-dd-action-name": actionName,
  fitToAnchor = false,
  dropdownPlacement,
  dropdownOffsetY,
  flip,
  searchString,
  setSearchString,
  darkMode,
  searchPlaceholder,
  size = "xs",
  listInset = true,
  itemSelected,
  preventCloseOnAnchorClick = false,
  dangerousStyleThatShouldOnlyBeUsedForMerchantBranding,
  disableKeyboardNavigation = false,
  onItemMouseEnter,
  onItemMouseLeave,
  uiSelector,
  onEndReached,
  ...redoListProps
}: {
  "data-dd-action-name"?: string;
  searchString?: string;
  setSearchString?: (search: string) => void;
  searchPlaceholder?: string;
  dropdownOpen: boolean;
  setDropdownOpen: (open: boolean) => void;
  dropdownAnchor: HTMLElement | null;
  fitToAnchor?: boolean;
  dropdownPlacement?: PopperPlacementType;
  dropdownOffsetY?: number;
  flip?: boolean;
  darkMode?: boolean;
  listInset?: boolean;
  size?: RedoDropdownInputSize;
  preventCloseOnAnchorClick?: boolean;
  dangerousStyleThatShouldOnlyBeUsedForMerchantBranding?: React.CSSProperties;
  disabledTooltips?: Record<string | number, string>;
  disableKeyboardNavigation?: boolean;
  onItemMouseEnter?: (item: RedoListItem<T>) => void;
  onItemMouseLeave?: () => void;
  uiSelector?: string;
} & Omit<
  RedoListProps<T>,
  "focusedIndex" | "setFocusedIndex" | "refToListenTo" | "size"
>) {
  const { t } = useTranslation();
  const [focusedIndex, setFocusedIndex] = useState<number | undefined>(
    undefined,
  );

  const [searchRef, setSearchRef] = useState<HTMLDivElement | null>(null);

  const [clickAwayEvent, setClickAwayEvent] = useState<undefined | false>();

  const listOverrideStyles: CSSProperties = {
    ...dangerousStyleThatShouldOnlyBeUsedForMerchantBranding,
    ...(dangerousStyleThatShouldOnlyBeUsedForMerchantBranding
      ? { padding: 0 }
      : {}),
  };

  const dropdownContainerStyles: CSSProperties = {
    ...listOverrideStyles,
    ...(dangerousStyleThatShouldOnlyBeUsedForMerchantBranding?.backgroundColor
      ? {
          backgroundColor:
            dangerousStyleThatShouldOnlyBeUsedForMerchantBranding.backgroundColor,
        }
      : {}),
  };

  const dropdownFlexProps: FlexProps = {
    ...(dangerousStyleThatShouldOnlyBeUsedForMerchantBranding?.outlineColor
      ? {
          style: {
            borderColor:
              dangerousStyleThatShouldOnlyBeUsedForMerchantBranding.outlineColor,
          },
        }
      : {}),
  };

  // We need to delay the click event changing by a render cycle
  // to prevent the click that opened it from immediately closing it
  useEffect(() => {
    if (dropdownOpen) {
      setClickAwayEvent(undefined);
    } else {
      setClickAwayEvent(false);
    }
  }, [dropdownOpen]);

  const handleItemSelected = useHandler(
    (args: {
      item: RedoListItem<T>;
      source: RedoListItemSelectedSource;
      index: number;
      selected: boolean;
    }) => {
      setDropdownOpen(false);
      itemSelected(args);
    },
  );

  const handleKeyDown = useHandler((e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setDropdownOpen(false);
      dropdownAnchor?.focus();
    }
  });

  // The virtualized list owns its own scroll area and reports end-reached
  // itself; the plain list scrolls inside the Dropdown, so watch that here.
  const handleDropdownScroll = useHandler((event: UIEvent<HTMLDivElement>) => {
    if (!onEndReached || redoListProps.virtualize) {
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight <= END_REACHED_THRESHOLD_PX) {
      onEndReached();
    }
  });

  return (
    <ClickAwayListener
      mouseEvent={clickAwayEvent}
      onClickAway={(event) => {
        if (
          preventCloseOnAnchorClick &&
          dropdownAnchor &&
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          dropdownAnchor.contains(event.target as Node)
        ) {
          return;
        }
        setDropdownOpen(false);
      }}
      touchEvent={clickAwayEvent}
    >
      <Dropdown
        anchor={dropdownAnchor}
        constrainHeight
        darkMode={darkMode}
        fitToAnchor={fitToAnchor}
        flexProps={{ p: "none", ...dropdownFlexProps }}
        flip={flip}
        offsetY={dropdownOffsetY}
        onScroll={onEndReached ? handleDropdownScroll : undefined}
        open={dropdownOpen}
        placement={dropdownPlacement}
      >
        <Flex
          data-arbiter="RedoListDropdown"
          data-dd-action-name={actionName}
          data-portal-selector="redo-dropdown"
          data-ui={uiSelector}
          dir="column"
          gap="none"
          onKeyDown={handleKeyDown}
          style={dropdownContainerStyles}
        >
          {setSearchString && (
            <Flex
              borderBottomWidth="1px"
              borderColor="primary"
              borderStyle="solid"
              className={classNames(
                css.searchHeader,
                darkMode && css.searchHeaderDark,
              )}
              px="lg"
              py="md"
              style={dangerousStyleThatShouldOnlyBeUsedForMerchantBranding}
            >
              <BaseRedoInput
                autoFocus
                placeholder={
                  searchPlaceholder ||
                  t("Search items...", { context: "list search" })
                }
                ref={setSearchRef}
                setValue={setSearchString}
                size={listItemSizeToInputSize[size]}
                value={searchString || ""}
              />
            </Flex>
          )}
          <Flex px={listInset ? "lg" : "none"} py={listInset ? "md" : "none"}>
            <RedoList
              customStyles={
                dangerousStyleThatShouldOnlyBeUsedForMerchantBranding
              }
              focusedIndex={
                disableKeyboardNavigation ? undefined : focusedIndex
              }
              itemSelected={handleItemSelected}
              onEndReached={onEndReached}
              onItemMouseEnter={onItemMouseEnter}
              onItemMouseLeave={onItemMouseLeave}
              refToListenTo={searchRef || dropdownAnchor}
              setFocusedIndex={
                disableKeyboardNavigation ? () => {} : setFocusedIndex
              }
              size={dropdownSizeToListSize[size]}
              {...redoListProps}
            />
          </Flex>
        </Flex>
      </Dropdown>
    </ClickAwayListener>
  );
});

const END_REACHED_THRESHOLD_PX = 48;

export const listItemSizeToInputSize: Record<
  RedoDropdownInputSize,
  RedoInputSize
> = { xxs: "sm", xs: "sm", sm: "sm", md: "md", lg: "md" };

export const dropdownSizeToListSize: Record<
  RedoDropdownInputSize,
  RedoListItemSize
> = { xxs: "sm", xs: "sm", sm: "lg", md: "lg", lg: "lg" };
